import { motion } from 'framer-motion';
import { useCallback, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, CheckOutline, Flex, Text, XOutline } from '@bonusly/design-system';

import type { ApprovalDecision } from '../hooks/use-bizy-global-chat';

export interface ApprovalCardMetadataItem {
	key: string;
	value: string;
}

export interface BizyApprovalCardProps {
	title: string;
	children?: ReactNode;
	metadata?: ApprovalCardMetadataItem[];
	footer?: string;
	confirmLabel?: string;
	receiptLabel?: string;
	choice?: ApprovalDecision;
	isDisabled?: boolean;
	onConfirm?: () => void;
}

function ApprovalCardReceipt({ choice, receiptLabel }: { choice: ApprovalDecision; receiptLabel?: string }) {
	const { t } = useTranslation('admin_bizy');
	const isApproved = choice === 'approved';
	const label = receiptLabel ?? (isApproved ? t('approval_card.approved') : t('approval_card.denied'));

	return (
		<motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.2, ease: 'easeOut' }}>
			<Flex align="center" gap={2} px={3} py={2.5} borderRadius={12} bg={isApproved ? 'success.bg' : 'bg.subtle'}>
				<Flex align="center" justify="center" boxSize={5} borderRadius="full" bg={isApproved ? 'success.icon' : 'secondary.bg'} flexShrink={0}>
					{isApproved ? <CheckOutline boxSize={3} color="text.onColor" /> : <XOutline boxSize={3} color="text.soft" />}
				</Flex>
				<Text fontSize="sm" fontWeight="medium" color={isApproved ? 'success.text' : 'text.soft'}>
					{label}
				</Text>
			</Flex>
		</motion.div>
	);
}

export function BizyApprovalCard({ title, children, metadata, footer, confirmLabel, receiptLabel, choice: externalChoice, isDisabled = false, onConfirm }: BizyApprovalCardProps) {
	const { t } = useTranslation('admin_bizy');
	const [localChoice, setLocalChoice] = useState<ApprovalDecision | undefined>();
	const choice = externalChoice ?? localChoice;

	const handleConfirm = useCallback(() => {
		setLocalChoice('approved');
		onConfirm?.();
	}, [onConfirm]);

	if (choice) {
		return <ApprovalCardReceipt choice={choice} receiptLabel={receiptLabel} />;
	}

	return (
		<motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, ease: 'easeOut' }}>
			<Box border="1px solid" borderColor="border.default" borderRadius={12} bg="bg.surface" overflow="hidden">
				<Box px={3} py={2.5} borderBottom="1px solid" borderColor="border.default">
					<Text fontSize="sm" fontWeight="semibold" color="text.default">
						{title}
					</Text>
				</Box>

				{(children || (metadata && metadata.length > 0)) && (
					<Box px={3} py={3}>
						{children}

						{metadata && metadata.length > 0 && (
							<Flex direction="column" gap={1} mt={children ? 2 : 0}>
								{metadata.map((item) => (
									<Flex key={item.key} justify="space-between" align="baseline" gap={3}>
										<Text fontSize="xs" color="text.softer" flexShrink={0}>
											{item.key}
										</Text>
										<Text fontSize="xs" color="text.default" textAlign="right" noOfLines={2}>
											{item.value}
										</Text>
									</Flex>
								))}
							</Flex>
						)}
					</Box>
				)}

				<Flex align="center" justify="space-between" px={3} pb={3} pt={!(children || (metadata && metadata.length > 0)) ? 3 : 0}>
					<Text fontSize="xs" color="text.softest">
						{footer}
					</Text>
		<Button size="sm" onClick={handleConfirm} isDisabled={isDisabled} flexShrink={0}>
						{confirmLabel ?? t('approval_card.approve')}
					</Button>
				</Flex>
			</Box>
		</motion.div>
	);
}
